import TurndownService from 'turndown';

/**
 * Detects if content is HTML (legacy) or markdown
 * @param {string} content - The content to check
 * @returns {boolean} - True if content appears to be HTML
 */
export const isHtml = (content) => {
	if (!content || typeof content !== 'string') return false;

	// Check for common HTML patterns
	const hasHtmlTags = /<[^>]+>/.test(content);
	const hasClosingTags =
		content.includes('</p>') ||
		content.includes('</div>') ||
		content.includes('</h1>') ||
		content.includes('</h2>') ||
		content.includes('</h3>') ||
		content.includes('</ul>') ||
		content.includes('</ol>') ||
		content.includes('</li>') ||
		content.includes('</strong>') ||
		content.includes('</em>') ||
		content.includes('</a>') ||
		content.includes('</span>');

	return hasHtmlTags && hasClosingTags;
};

// Create turndown service with custom rules
const createTurndownService = () => {
	const turndownService = new TurndownService({
		headingStyle: 'atx', // Use # for headings
		bulletListMarker: '-', // Use - for bullet lists
		codeBlockStyle: 'fenced', // Use ``` for code blocks
		emDelimiter: '*', // Use * for emphasis
		strongDelimiter: '**', // Use ** for bold
	});

	// Keep images with their src
	turndownService.addRule('images', {
		filter: 'img',
		replacement: (content, node) => {
			const alt = node.getAttribute('alt') || '';
			const src = node.getAttribute('src') || '';
			return src ? `![${alt}](${src})` : '';
		},
	});

	return turndownService;
};

let turndownService = null;

/**
 * Converts HTML content to Markdown
 * @param {string} html - The HTML content to convert
 * @returns {string} - The markdown content
 */
export const htmlToMarkdown = (html) => {
	if (!html || typeof html !== 'string') return '';

	// If it doesn't look like HTML, return as-is
	if (!isHtml(html)) return html;

	// Lazy initialize turndown service
	if (!turndownService) {
		turndownService = createTurndownService();
	}

	try {
		const markdown = turndownService.turndown(html);
		// Clean up extra whitespace
		return markdown.trim();
	} catch (error) {
		console.error('Failed to convert HTML to Markdown:', error);
		// Return original content if conversion fails
		return html;
	}
};

/**
 * Prepares content for editing - converts HTML to Markdown if needed
 * @param {string} content - The content to prepare
 * @returns {string} - Markdown content ready for editing
 */
export const prepareForEdit = (content) => {
	if (!content) return '';
	return isHtml(content) ? htmlToMarkdown(content) : content;
};
